<template>
  <div ref="utterance" class="msg-container relative">
    <div :class="['msg flex py-5', direction === 'right' ? 'owner' : '']">
      <div
        v-tooltip="{ content: content.name, show: tooltipVisible }"
        class="msg-profile flex-shrink-0 relative"
        @touchstart="handleImgClick"
      >
        <img :src="avatar" class="w-10 h-10 rounded-full object-cover" />
      </div>
      <div class="msg-content flex flex-col items-start max-w-7/10">
        <div class="msg-text relative">
          <div class="content-name">
            {{ content.name }}
          </div>
          <MessageIcon
            :color="direction === 'right' ? '#6965f5' : undefined"
            class="message-icon"
          />
          <div class="message message-bg">
            <div class="w-10 mr-4" @click.stop="handelAction">
              <PlayIcon :isLeft="direction !== 'right'" :isPaused="paused" />
            </div>
            <div class="relative w-32 sm:w-40 mr-4">
              <Ripple
                :paused="paused"
                :progress="progress"
                :isLeft="direction !== 'right'"
                :contentId="content.$id"
                @progressMove="handelProgressMove"
                @dragging="handelDrag"
              />
            </div>
            <div class="duration select-none">
              {{ paused && !dragging ? durationString : currentTimeString }}
            </div>
            <audio
              ref="audio"
              :src="content.url"
              @canplay="audioCanplay"
              @loadedmetadata="handelOnLoad"
              @timeupdate="handelTimeupdate"
              @play="handelPlay"
              @pause="handelPlay"
              @error="error"
            ></audio>
            <i
              v-if="!content.isPlay"
              :class="direction !== 'right' ? 'right-message-red' : 'left-message-red'"
            ></i>
          </div>
          <div v-if="hasLoading" class="shade">
            <loading class="mt-1" />
          </div>
          <div v-if="audioError" class="shade">音频文件貌似有些问题哦</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Content } from '@forchange/storytime';
import { Vue, Component, Prop } from 'vue-property-decorator';
import Loading from '@/components/loading';
import Explanation from '../Explanation';
import Ripple from './ripple.vue';
import PlayIcon from './assets/playIcon.vue';
import MessageIcon from '@/components/resources/dialog/Utterance/assets/MessageIcon.vue';
import * as Sentry from '@sentry/vue';
import { namespace } from 'vuex-class';
import { Environment } from '@/store/modules/common/types/index';
import { UserInfo } from '@/store/modules/common/types';

const userModule = namespace('common');

@Component({
  components: {
    Loading,
    Explanation,
    Ripple,
    PlayIcon,
    MessageIcon
  }
})
export default class Audio extends Vue {
  @Prop({ type: Object }) content!: Content & {
    name: string;
    url: string;
    isPlay: boolean;
    currentTime: number;
  };

  @Prop({ type: Array }) characters!: Array<{
    name: string;
    avatar: string;
    direction?: 'right' | 'left';
  }>;

  @Prop({ type: Boolean }) isroll!: boolean;

  @userModule.State('environment') environment!: Environment;
  @userModule.State('version') version!: string;
  @userModule.State('userInfo') userInfo!: UserInfo;
  tooltipVisible = false;
  timeoutId: number | undefined;

  duration = 1;
  currentTime = 0;
  paused = true;
  audioError = false;

  dragging = false;

  get durationString() {
    return this.formatTime(Math.floor(this.duration));
  }
  get currentTimeString() {
    return this.formatTime(Math.floor(this.currentTime));
  }

  get progress() {
    return Math.floor((this.currentTime / this.duration) * 10000) / 100;
  }

  hasLoading = true;

  get avatar() {
    const avatar = this.characters.filter((item) => item.name === this.content.name)[0]?.avatar;
    avatar && this.$emit('getCurrentAvatar', avatar);
    return avatar;
  }

  // 方向: left 是默认，right 头像信息将在右侧出现
  get direction() {
    const direction = this.characters.filter((item) => item.name === this.content.name)[0]
      ?.direction;
    return direction;
  }

  mounted() {
    this.initAudioCurrentTime();
  }

  initAudioCurrentTime() {
    const audio = this.$refs.audio as HTMLAudioElement;
    this.content.currentTime && (audio.currentTime = this.content.currentTime);
  }

  handleImgClick(e: MouseEvent) {
    e.preventDefault();
    this.timeoutId && clearTimeout(this.timeoutId);
    this.tooltipVisible = !this.tooltipVisible;
    this.tooltipVisible &&
      (this.timeoutId = window.setTimeout(() => {
        this.tooltipVisible = false;
      }, 1000));
  }

  handelOnLoad(e: Event) {
    const audio = e.target as HTMLAudioElement;
    this.duration = audio.duration;
    this.hasLoading = false;
  }

  formatTime(time: number) {
    const seconds = Math.floor(time % 60);
    const minutes = Math.floor((time - seconds) / 60);
    if (isNaN(seconds) || isNaN(minutes)) return '--:--';
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  handelTimeupdate(e: Event) {
    this.currentTime = (e.target as HTMLAudioElement).currentTime;
  }

  handelPlay(e: Event) {
    this.paused = (e.target as HTMLAudioElement).paused;
    if (this.content.isPlay) return;
    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        isPlay: true,
        touchLoading: false
      }
    });
  }

  handelAction() {
    const audio = this.$refs.audio as HTMLAudioElement;
    if (!this.paused) {
      window.storyteller.updateAssignContent({
        $id: this.content.$id,
        $kind: this.content.$kind,
        ...{
          currentTime: audio.currentTime
        }
      });
    }
    this.paused ? audio.play() : audio.pause();
  }

  error(e: any) {
    this.hasLoading = false;
    this.audioError = true;
    Sentry.setContext('userinfo', this.userInfo);
    Sentry.setContext('surfer', {
      'surfer-version': this.version,
      'surfer-courseId':
        this.environment?.kind === 'public-preview' ? this.environment?.sharedKey : ''
    });
    Sentry.setContext('audio-info', {
      src: this.content.url
    });
    Sentry.captureMessage(`[Audio fail]: ${e.message ?? e}`);
  }

  async audioCanplay(e: Event) {
    const firsthandAudio = e.target as HTMLAudioElement;
    this.duration = firsthandAudio.duration;
  }

  handelProgressMove(progress: number) {
    if (this.duration === Infinity) return;
    const audioEl = this.$refs.audio as HTMLAudioElement;
    const currentTime = this.duration * progress;
    audioEl.currentTime = currentTime;
  }

  handelDrag(drag: boolean) {
    this.dragging = drag;
  }

  async beforeDestroy() {
    const audio = this.$refs.audio as HTMLAudioElement;
    // 播放的情况加增加请求
    if (!this.paused && audio) {
      window.storyteller.updateAssignContent({
        $id: this.content.$id,
        $kind: this.content.$kind,
        ...{
          currentTime: audio.currentTime
        }
      });
    }
  }
}
</script>

<style scoped>
.owner {
  @apply flex-row-reverse;
}

.owner .msg-content {
  @apply items-end;
}

.owner .message-bg {
  @apply text-white;
  background-color: #6965f5;
}

.message-bg {
  @apply bg-base text-black;
}

.message {
  @apply relative flex items-center px-4 py-3 max-w-sm md:max-w-full rounded-motif;
  min-height: 44px;
}

.message > div {
  @apply flex items-center h-10;
}

.message-icon {
  @apply absolute left-0 top-10;
  transform: translateX(-100%);
}

.owner .message-icon {
  @apply absolute left-full top-10;
  transform: rotateY(180deg) translateX(15%);
}

.content-name {
  @apply mb-1 w-full truncate;
  color: #808490;
  font-size: 12px;
}

.owner .content-name {
  @apply text-right;
}

.msg-text {
  @apply max-w-full ml-2;
}

.owner .msg-text {
  @apply mr-2;
}

.left-message-red {
  content: ' ';
  @apply block absolute -left-2 -top-2 w-2 h-2 bg-red-600 rounded-full;
}

.right-message-red {
  content: ' ';
  @apply block absolute -right-2 -top-2 w-2 h-2 bg-red-600 rounded-full;
}

.duration {
  min-width: 3rem;
}

.shade {
  @apply absolute bottom-0 left-0 w-full h-16 flex items-center justify-center bg-base rounded-motif;
}

.owner .shade {
  background-color: #6965f5;
}
</style>
